import React from 'react';
import { motion } from 'framer-motion';

const EmotionalShoppingTrackerLanding = ({ goToInput, theme }) => {
  // Theme-based button styling
  const buttonStyles = {
    mint: "bg-emerald-500 hover:bg-emerald-600",
    pink: "bg-pink-500 hover:bg-pink-600",
    lavender: "bg-purple-500 hover:bg-purple-600"
  };

  // Theme-based accent styling
  const accentStyles = {
    mint: "border-emerald-500 text-emerald-600",
    pink: "border-pink-500 text-pink-600",
    lavender: "border-purple-500 text-purple-600"
  };

  return (
    <motion.div
      className="bg-white dark:bg-gray-800 rounded-3xl shadow-xl p-6 mb-8"
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.3 }}
    >
      {/* SEO-optimized heading structure */}
      <h1 className="text-3xl font-bold text-center mb-6 dark:text-white">
        Emotional Shopping Tracker: Understand Your Mood-Based Purchases
      </h1>
      
      {/* Hero section with main CTA */}
      <div className="bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-900/30 dark:to-indigo-900/30 rounded-xl p-6 mb-8">
        <div className="flex flex-col md:flex-row items-center">
          <div className="mb-6 md:mb-0 md:mr-6 text-center md:text-left">
            <h2 className="text-2xl font-bold mb-3 dark:text-white">Track How Your Emotions Influence What You Buy</h2>
            <p className="text-gray-700 dark:text-gray-300 mb-4">
              Discover the connection between your moods and spending habits. Break the cycle of emotional shopping and reduce buyer's remorse.
            </p>
            <motion.button
              onClick={goToInput}
              className={`px-6 py-3 rounded-xl text-white font-semibold ${buttonStyles[theme]}`}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              Track Your First Purchase
            </motion.button>
          </div>
          <div className="text-8xl">😊🛍️😔</div>
        </div>
      </div>
      
      {/* Emotional Shopping Stats Section */}
      <section className="mb-8 bg-gray-50 dark:bg-gray-700 rounded-xl p-5">
        <h2 className="text-xl font-bold mb-4 dark:text-white">The Impact of Emotions on Shopping</h2>
        
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
          <div>
            <div className="text-2xl font-bold dark:text-white">70%</div>
            <p className="text-sm text-gray-600 dark:text-gray-400">of purchases are influenced by mood</p>
          </div>
          
          <div>
            <div className="text-2xl font-bold dark:text-white">68%</div>
            <p className="text-sm text-gray-600 dark:text-gray-400">of "sad shopping" ends in regret</p>
          </div>
          
          <div>
            <div className="text-2xl font-bold dark:text-white">65%</div>
            <p className="text-sm text-gray-600 dark:text-gray-400">higher spending when shopping while bored</p>
          </div>
          
          <div>
            <div className="text-2xl font-bold dark:text-white">3x</div>
            <p className="text-sm text-gray-600 dark:text-gray-400">more returns from emotional purchases</p>
          </div>
        </div>
      </section>
      
      {/* Common Emotional Shopping Triggers */}
      <section className="mb-8">
        <h2 className="text-2xl font-bold mb-4 dark:text-white">Common Emotional Shopping Triggers</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4">
            <div className="text-center mb-3">
              <span className="text-4xl">😔</span>
              <h3 className="font-bold mt-2 dark:text-white">Sadness & Depression</h3>
            </div>
            <p className="text-gray-600 dark:text-gray-300 text-sm">
              Shopping as a pick-me-up when feeling down or empty. Often leads to guilt and regret afterward.
            </p>
          </div>
          
          <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4">
            <div className="text-center mb-3">
              <span className="text-4xl">😑</span>
              <h3 className="font-bold mt-2 dark:text-white">Boredom</h3>
            </div>
            <p className="text-gray-600 dark:text-gray-300 text-sm">
              Browsing online stores or malls to fill time and create stimulation. Results in unnecessary purchases.
            </p>
          </div>
          
          <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4">
            <div className="text-center mb-3">
              <span className="text-4xl">😤</span>
              <h3 className="font-bold mt-2 dark:text-white">Stress & Anxiety</h3>
            </div>
            <p className="text-gray-600 dark:text-gray-300 text-sm">
              Using shopping as a form of escape or relief from overwhelming feelings. Creates temporary calm.
            </p>
          </div>
          
          <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4">
            <div className="text-center mb-3">
              <span className="text-4xl">🎉</span>
              <h3 className="font-bold mt-2 dark:text-white">Celebration</h3>
            </div>
            <p className="text-gray-600 dark:text-gray-300 text-sm">
              Rewarding yourself for achievements with purchases. Can lead to overspending on "deserved" treats.
            </p>
          </div>
          
          <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4">
            <div className="text-center mb-3">
              <span className="text-4xl">👀</span>
              <h3 className="font-bold mt-2 dark:text-white">FOMO</h3>
            </div>
            <p className="text-gray-600 dark:text-gray-300 text-sm">
              Fear of missing out drives purchases of trending items or limited editions, regardless of need.
            </p>
          </div>
          
          <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4">
            <div className="text-center mb-3">
              <span className="text-4xl">🍸</span>
              <h3 className="font-bold mt-2 dark:text-white">Intoxication</h3>
            </div>
            <p className="text-gray-600 dark:text-gray-300 text-sm">
              Shopping while under the influence leads to reduced inhibitions and impulse purchases.
            </p>
          </div>
        </div>
      </section>
      
      {/* How the tool works */}
      <section className="mb-8">
        <h2 className="text-2xl font-bold mb-4 dark:text-white">How Our Emotional Shopping Tracker Works</h2>
        <div className="space-y-4">
          <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4">
            <h3 className="font-bold flex items-center dark:text-white">
              <span className="bg-blue-100 dark:bg-blue-800 text-blue-800 dark:text-blue-200 h-6 w-6 rounded-full flex items-center justify-center mr-2">1</span>
              Log Your Purchase & Mood
            </h3>
            <p className="text-gray-600 dark:text-gray-300 ml-8">
              Record what you bought, how much it cost, and how you were feeling at the time of purchase.
            </p>
          </div>
          
          <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4">
            <h3 className="font-bold flex items-center dark:text-white">
              <span className="bg-blue-100 dark:bg-blue-800 text-blue-800 dark:text-blue-200 h-6 w-6 rounded-full flex items-center justify-center mr-2">2</span>
              Get Your Emotional Shopping Score
            </h3>
            <p className="text-gray-600 dark:text-gray-300 ml-8">
              Our algorithm calculates how much your emotional state influenced your purchase decision.
            </p>
          </div>
          
          <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4">
            <h3 className="font-bold flex items-center dark:text-white">
              <span className="bg-blue-100 dark:bg-blue-800 text-blue-800 dark:text-blue-200 h-6 w-6 rounded-full flex items-center justify-center mr-2">3</span>
              Discover Your Emotional Spending Patterns
            </h3>
            <p className="text-gray-600 dark:text-gray-300 ml-8">
              Visualize connections between your moods and spending habits through personalized insights.
            </p>
          </div>
          
          <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4">
            <h3 className="font-bold flex items-center dark:text-white">
              <span className="bg-blue-100 dark:bg-blue-800 text-blue-800 dark:text-blue-200 h-6 w-6 rounded-full flex items-center justify-center mr-2">4</span>
              Get Tailored Strategies to Break the Cycle
            </h3>
            <p className="text-gray-600 dark:text-gray-300 ml-8">
              Receive personalized recommendations to manage emotional triggers and make mindful purchasing decisions.
            </p>
          </div>
        </div>
      </section>
      
      {/* Emotional Shopper Types */}
      <section className="mb-8">
        <h2 className="text-2xl font-bold mb-4 dark:text-white">Discover Your Emotional Shopper Type</h2>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4">
            <div className="text-center mb-3">
              <span className="text-3xl">😔🛍️</span>
              <h3 className="font-bold mt-2 dark:text-white">The Mood Lifter</h3>
            </div>
            <p className="text-gray-600 dark:text-gray-300 text-sm">
              You shop to escape negative emotions, using purchases as a quick way to boost your mood and feel temporarily better.
            </p>
          </div>
          
          <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4">
            <div className="text-center mb-3">
              <span className="text-3xl">😑💻</span>
              <h3 className="font-bold mt-2 dark:text-white">The Boredom Browser</h3>
            </div>
            <p className="text-gray-600 dark:text-gray-300 text-sm">
              You turn to online shopping or mall trips when bored, using the stimulation of browsing and buying to fill empty time.
            </p>
          </div>
          
          <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4">
            <div className="text-center mb-3">
              <span className="text-3xl">🎉💎</span>
              <h3 className="font-bold mt-2 dark:text-white">The Celebratory Splurger</h3>
            </div>
            <p className="text-gray-600 dark:text-gray-300 text-sm">
              You use shopping as a reward system, making significant purchases to celebrate achievements or mark special occasions.
            </p>
          </div>
          
          <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4">
            <div className="text-center mb-3">
              <span className="text-3xl">👀🏃</span>
              <h3 className="font-bold mt-2 dark:text-white">The FOMO Follower</h3>
            </div>
            <p className="text-gray-600 dark:text-gray-300 text-sm">
              Your purchases are driven by fear of missing out, buying trendy items quickly before they're gone, regardless of need.
            </p>
          </div>
        </div>
      </section>
      
      {/* Testimonials section */}
      <section className="mb-8">
        <h2 className="text-2xl font-bold mb-4 dark:text-white">How Others Changed Their Emotional Shopping Habits</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4">
            <div className="flex flex-col h-full">
              <p className="text-gray-600 dark:text-gray-300 italic mb-4">
                "I never realized how often I shopped when I was feeling down until I started tracking. In just two months, I've saved over $600 by finding other ways to manage my sadness."
              </p>
              <div className="mt-auto">
                <p className="font-semibold dark:text-white">Melissa T.</p>
                <p className="text-xs text-gray-500 dark:text-gray-400">Former Mood Lifter Shopper</p>
              </div>
            </div>
          </div>
          
          <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4">
            <div className="flex flex-col h-full">
              <p className="text-gray-600 dark:text-gray-300 italic mb-4">
                "The pattern was clear - I'd browse online stores whenever I was bored at home. Now I have alternative activities ready for those moments, and my impulse purchases have dropped 90%."
              </p>
              <div className="mt-auto">
                <p className="font-semibold dark:text-white">James L.</p>
                <p className="text-xs text-gray-500 dark:text-gray-400">Reformed Boredom Browser</p>
              </div>
            </div>
          </div>
        </div>
      </section>
      
      {/* FAQ Section */}
      <section className="mb-8">
        <h2 className="text-2xl font-bold mb-4 dark:text-white">Common Questions About Emotional Shopping</h2>
        <div className="space-y-4">
          <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4">
            <h3 className="font-bold dark:text-white">What exactly is emotional shopping?</h3>
            <p className="text-gray-600 dark:text-gray-300 mt-2">
              Emotional shopping is making purchase decisions primarily based on your current mood or feelings rather than practicality, need, or budget. It includes shopping to improve mood, celebrate, relieve boredom, or reduce anxiety.
            </p>
          </div>
          
          <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4">
            <h3 className="font-bold dark:text-white">How can I identify my emotional spending triggers?</h3>
            <p className="text-gray-600 dark:text-gray-300 mt-2">
              Our tracker helps you record your mood with each purchase, revealing patterns over time. Pay attention to when you get urges to shop - are you stressed, bored, sad, or celebrating? These insights help identify your specific emotional triggers.
            </p>
          </div>
          
          <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4">
            <h3 className="font-bold dark:text-white">Is it bad to shop when I'm feeling happy or celebrating?</h3>
            <p className="text-gray-600 dark:text-gray-300 mt-2">
              Celebratory shopping isn't inherently negative, but it becomes problematic when it exceeds your budget or when purchases don't align with your values or needs. Our tool helps ensure your celebratory purchases remain mindful and guilt-free.
            </p>
          </div>
          
          <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4">
            <h3 className="font-bold dark:text-white">What alternatives can I try instead of emotional shopping?</h3>
            <p className="text-gray-600 dark:text-gray-300 mt-2">
              Based on your emotional shopper type, our tool suggests personalized alternatives: physical activity for stress, creative hobbies for boredom, social connection for sadness, and mindfulness practices for impulsivity. We help you build a toolkit of non-shopping responses.
            </p>
          </div>
        </div>
      </section>
      
      {/* CTA section */}
      <section className="bg-gradient-to-r from-blue-50 to-purple-50 dark:from-blue-900/30 dark:to-purple-900/30 rounded-xl p-6 text-center">
        <h2 className="text-2xl font-bold mb-3 dark:text-white">Ready to Understand Your Emotional Shopping?</h2>
        <p className="text-gray-700 dark:text-gray-300 mb-4">
          Start tracking your mood-based purchases today. All data stays private on your device.
        </p>
        <motion.button
          onClick={goToInput}
          className={`px-8 py-3 rounded-xl text-white font-semibold ${buttonStyles[theme]}`}
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
        >
          Track Your First Purchase Now
        </motion.button>
      </section>
      
      {/* Rich SEO footer content */}
      <footer className="mt-8 pt-6 border-t border-gray-200 dark:border-gray-700">
        <div className="text-sm text-gray-600 dark:text-gray-400">
          <h2 className="text-lg font-semibold mb-2 text-gray-800 dark:text-gray-200">About Emotional Shopping</h2>
          <p className="mb-3">
            Emotional shopping refers to making purchase decisions based primarily on your current feelings rather than practical considerations. This behavior can lead to regrettable purchases, budget issues, and a cycle of mood-dependent consumption.
          </p>
          <p className="mb-3">
            Our Emotional Shopping Tracker helps you understand the connection between your moods and spending habits. By identifying specific emotional triggers, you can develop healthier responses to feelings like sadness, boredom, or excitement without resorting to unnecessary purchases.
          </p>
          <p>
            Whether you shop when sad to boost your mood, browse online when bored, or overspend when celebrating, our free tool helps you recognize patterns and make more mindful, emotionally-aware decisions about your purchases.
          </p>
        </div>
      </footer>
    </motion.div>
  );
};

export default EmotionalShoppingTrackerLanding;
